import React, { useState } from 'react';
import { ChevronDown, AlertCircle, CheckCircle } from 'lucide-react';

const EXAMPLES = [
  {
    title: 'Rent Payment Terms',
    original: '"Rent must be paid on the first day of each month or tenant is in breach."',
    issue: 'Too strict; violates Renters Rights Bill fairness requirements. Creates immediate breach for any delay.',
    corrected: '"Rent is due on the first day of each month. Payment received within 3 working days is considered on time. A notice to remedy is required before any breach claim."',
  },
  {
    title: 'Entry for Inspections',
    original: '"Landlord may enter the property at any time for inspections."',
    issue: 'Unlimited access violates tenant privacy rights and conflicts with Housing Act 1988.',
    corrected: '"Landlord may enter with 24 hours\' written notice for inspections, not more than twice per year, between 10am-6pm on working days."',
  },
  {
    title: 'End of Tenancy Condition',
    original: '"Property must be returned in pristine condition, same as new."',
    issue: 'Unrealistic standard; wear and tear must be excluded by law. Current clause would fail court challenge.',
    corrected: '"Property must be returned in a clean and tidy condition, with normal wear and tear excluded."',
  },
  {
    title: 'Liability Exclusion',
    original: '"Tenant assumes all liability for any damage to the property, regardless of cause."',
    issue: 'Overly broad; cannot exclude landlord liability for statutory obligations and structural matters.',
    corrected: '"Tenant is responsible for damage caused by their negligence or breach. Landlord is responsible for structural damage and issues covered by building insurance."',
  },
];

export function ClauseExamples() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="py-20 sm:py-28 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            See Real Clause Improvements
          </h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            Examples of typical problematic clauses we encounter and how we fix them
          </p>
        </div>

        <div className="space-y-4">
          {EXAMPLES.map((example, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={example.title}
                className="animate-on-scroll bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-elevated transition-shadow duration-300"
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-200 ${isOpen ? 'bg-emerald-600' : 'bg-gray-100'}`}>
                      <span className={`text-xs font-bold ${isOpen ? 'text-white' : 'text-gray-600'}`}>{i + 1}</span>
                    </div>
                    <h3 className="font-semibold text-gray-900">{example.title}</h3>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && (
                  <div className="px-6 pb-6 animate-slide-down">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                          <div className="flex items-center gap-2 mb-3">
                            <AlertCircle className="w-4 h-4 text-red-600" />
                            <span className="text-xs font-bold text-red-700 uppercase tracking-wider">Original Clause (Problematic)</span>
                          </div>
                          <p className="text-sm text-red-900 leading-relaxed italic">{example.original}</p>
                        </div>
                        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                          <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-1">Issue</p>
                          <p className="text-sm text-amber-900 leading-relaxed">{example.issue}</p>
                        </div>
                      </div>

                      <div>
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 h-full">
                          <div className="flex items-center gap-2 mb-3">
                            <CheckCircle className="w-4 h-4 text-emerald-600" />
                            <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Corrected Version</span>
                          </div>
                          <p className="text-sm text-emerald-900 leading-relaxed italic mb-4">{example.corrected}</p>
                          <div className="inline-flex items-center gap-1.5 bg-emerald-100 border border-emerald-200 rounded-full px-3 py-1">
                            <CheckCircle className="w-3 h-3 text-emerald-700" />
                            <span className="text-xs font-semibold text-emerald-800">Compliant and legally sound</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
